import { Card } from "../common/Card";
import { money } from "../../utils/format";
import { formatDateOnly } from "../../services/hiveActions";

/**
 * The vendor summary strip (#254).
 *
 * Six figures at most, and which six depends on what the reader is allowed
 * to see. The counts are always there: "have you still got jars, did the
 * empties come back" is the restocking call, and it is the same call on
 * every tier.
 *
 * The money tiles appear only when the server sent money. It signals that
 * by OMITTING the keys, so this checks presence rather than truthiness --
 * a genuine outstanding of $0 is a settled vendor and has to show as one,
 * which a falsy check would hide (#235).
 */

function Tile({ label, value, hint, tone }) {
  return (
    <div style={{
      background: "var(--color-surface-muted, var(--color-bg-subtle))",
      borderRadius: "var(--radius-md, 10px)",
      padding: "var(--space-3)",
      minWidth: 0,
    }}>
      <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>{label}</div>
      <div style={{
        fontSize: "var(--font-size-xl, 1.6rem)",
        fontWeight: 600,
        lineHeight: 1.15,
        color: tone === "owed" ? "var(--color-danger, inherit)" : "inherit",
      }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>{hint}</div>
      ) : null}
    </div>
  );
}

function countOfHoney(bottles, bulkPounds) {
  const parts = [];
  if (bottles) parts.push(`${bottles} bottle${bottles === 1 ? "" : "s"}`);
  if (Number(bulkPounds)) parts.push(`${Number(bulkPounds)} lb bulk`);
  return parts.join(" · ");
}

export function VendorSummary({ summary, moneyVisibility }) {
  if (!summary) return null;
  // Presence, never truthiness. See the note at the top of this file.
  const showMoney = "outstanding" in summary;
  const lastPaid = summary.last_payment_date
    ? `Last paid ${formatDateOnly(summary.last_payment_date)}`
    : "No payments yet";

  return (
    <Card className="section-spacing">
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "var(--space-3)",
      }}>
        {showMoney && (
          <>
            <Tile
              label="Honey provided"
              value={money(summary.honey_provided)}
              hint={countOfHoney(summary.bottles_out, summary.bulk_pounds_out)}
            />
            <Tile label="Money received" value={money(summary.total_paid)} hint={lastPaid} />
            {/* Overpayment is a real state -- a deposit, or a customer
                rounding up -- so it reads as credit rather than as a
                negative debt, which looks like a bug. */}
            <Tile
              label="Outstanding"
              value={Number(summary.outstanding) < 0
                ? `${money(-summary.outstanding)} in credit`
                : money(summary.outstanding)}
              tone={Number(summary.outstanding) > 0 ? "owed" : undefined}
              hint={Number(summary.returned_value) ? `after ${money(summary.returned_value)} returned` : undefined}
            />
          </>
        )}
        <Tile
          label="Bottles out"
          value={summary.bottles_out ?? 0}
          hint={!showMoney && Number(summary.bulk_pounds_out)
            ? `plus ${Number(summary.bulk_pounds_out)} lb bulk`
            : undefined}
        />
        <Tile
          label="Bottles returned"
          value={summary.bottles_returned ?? 0}
          hint={summary.bottles_returned ? "unsold" : undefined}
        />
        <Tile label="Empties returned" value={summary.empties_returned ?? 0} hint="jars" />
        {/* Without money, the payment DATE is what is left of the money
            story, and it is genuinely useful: it is what the chasing block
            is built from. */}
        {!showMoney && <Tile label="Last payment" value={lastPaid.replace("Last paid ", "")} />}
      </div>

      {/* Two different reasons for the same absence, and they need different
          sentences. Telling a helper to upgrade a plan they are not on is
          the mistake the vendor page already made once (#235). */}
      {moneyVisibility === "plan" && (
        <p style={{ marginBottom: 0, color: "var(--color-text-muted)" }}>
          Amounts, balances and receipts come with the Business plan.
        </p>
      )}
      {moneyVisibility === "hidden" && (
        <p style={{ marginBottom: 0, color: "var(--color-text-muted)" }}>
          Amounts are hidden on your account.
        </p>
      )}
    </Card>
  );
}
